import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Activity,
  AlertTriangle,
  ArrowLeft,
  BarChart3,
  BellRing,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  ClipboardCheck,
  Clock,
  Droplets,
  Gauge,
  Image as ImageIcon,
  RefreshCw,
  RotateCcw,
  Settings,
  Thermometer,
  Wrench,
  XCircle
} from 'lucide-react';
import { deviceSettingsAPI, unitAPI } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { hasAdminPermission } from '../utils/adminPermissions';
import { extractPhotoUrls, getInspectionDisplayOverallStatus, getInspectionDisplaySummary } from '../utils/operations';
import { formatStationDisplayId } from '../utils/stationId';
import {
  formatLocalizedDateTime,
  getFilterDisplayName,
  getInspectionFreeText,
  getInspectionItemText,
  getStatusLabel,
  getWaterTypeLabel,
  getWatermarkLabel
} from '../utils/i18nFormatters';
import UnitPreferencePanel from './UnitPreferencePanel';
import WaterQualityReadingCard from './WaterQualityReadingCard';

const TRANSACTION_PAGE_SIZE = 10;
const SUPPORTED_ALERT_TYPES = new Set(['offline', 'maintenance', 'fault', 'low_quality']);

const buildDeviceAlertHref = ({ unitId, alertType, status = 'open' } = {}) => {
  const params = new URLSearchParams();
  params.set('status', status === 'resolved' ? 'resolved' : 'open');
  if (unitId) params.set('unitId', unitId);
  if (alertType && SUPPORTED_ALERT_TYPES.has(alertType)) {
    params.set('alertType', alertType);
  }
  return `/device-alerts?${params.toString()}`;
};

const getUnitStatusClasses = (status) => (
  status === 'Active' || status === 'Online'
    ? 'bg-emerald-500/20 text-emerald-400'
    : 'bg-rose-500/20 text-rose-400'
);

const getReviewStatusClasses = (status) => {
  switch (status) {
    case 'approved':
      return 'bg-emerald-500/15 text-emerald-300';
    case 'rejected':
      return 'bg-rose-500/15 text-rose-300';
    case 'pending_review':
      return 'bg-amber-500/15 text-amber-300';
    default:
      return 'bg-slate-700 text-slate-300';
  }
};

const getOverallStatusClasses = () => 'bg-emerald-500/15 text-emerald-300';

const getWatermarkMeta = (t, status) => {
  switch (status) {
    case 'success':
      return {
        label: getWatermarkLabel(t, 'success'),
        className: 'bg-emerald-500/15 text-emerald-300'
      };
    case 'fallback_original':
      return {
        label: getWatermarkLabel(t, 'fallback_original'),
        className: 'bg-amber-500/15 text-amber-300'
      };
    case 'failed':
      return {
        label: getWatermarkLabel(t, 'failed'),
        className: 'bg-rose-500/15 text-rose-300'
      };
    default:
      return {
        label: getWatermarkLabel(t, 'unknown'),
        className: 'bg-slate-700 text-slate-300'
      };
  }
};

const getTransactionCardNumber = (tx) => (
  tx?.cardNumber || tx?.originCard?.cardNumber || tx?.physicalCardNumber || null
);

const isPhysicalTransaction = (tx) => (
  String(tx?.cardType || '').trim().toLowerCase() === 'physical'
);

const getTransactionCardDisplay = (tx) => {
  const cardNumber = getTransactionCardNumber(tx);
  if (cardNumber) return cardNumber;
  if (isPhysicalTransaction(tx)) return '--';
  return tx?.userPhone || tx?.rfid || '--';
};

const getTransactionCardTitle = (tx) => {
  const cardNumber = getTransactionCardNumber(tx);
  if (cardNumber) return `Card number: ${cardNumber}`;
  if (isPhysicalTransaction(tx)) return 'Physical card number is not available yet';
  return tx?.rfid ? `Virtual card / user identifier: ${tx.rfid}` : undefined;
};

const getTransactionCardTypeCode = (cardType) => {
  const normalized = String(cardType || '').trim().toLowerCase();
  if (normalized === 'physical') return 'P';
  if (normalized === 'virtual') return 'V';
  return normalized ? normalized.charAt(0).toUpperCase() : '--';
};

const getTransactionStatusCode = (status) => {
  const normalized = String(status || '').trim().toLowerCase();
  if (['completed', 'complete', 'success', 'succeeded'].includes(normalized)) return 'C';
  if (['failed', 'fail', 'error', 'cancelled', 'canceled'].includes(normalized)) return 'F';
  return normalized ? normalized.charAt(0).toUpperCase() : '--';
};

const parseCompactHardwareTime = (digits) => {
  if (!/^\d{14}$/.test(digits)) return null;

  const year = Number(digits.slice(0, 4));
  const month = Number(digits.slice(4, 6));
  const day = Number(digits.slice(6, 8));
  const hour = Number(digits.slice(8, 10));
  const minute = Number(digits.slice(10, 12));
  const second = Number(digits.slice(12, 14));
  const wallClockTime = Date.UTC(year, month - 1, day, hour, minute, second);
  const wallClockDate = new Date(wallClockTime);

  if (
    wallClockDate.getUTCFullYear() !== year ||
    wallClockDate.getUTCMonth() !== month - 1 ||
    wallClockDate.getUTCDate() !== day ||
    wallClockDate.getUTCHours() !== hour ||
    wallClockDate.getUTCMinutes() !== minute ||
    wallClockDate.getUTCSeconds() !== second
  ) {
    return null;
  }

  return new Date(wallClockTime - 7 * 60 * 60 * 1000);
};

const parseTransactionHardwareTime = (value) => {
  const hardwareTime = String(value || '').trim();
  if (/^\d{10}$|^\d{13}$/.test(hardwareTime)) {
    const numericTime = Number(hardwareTime);
    if (Number.isFinite(numericTime) && numericTime > 0) {
      return new Date(numericTime > 1e12 ? numericTime : numericTime * 1000);
    }
  }
  if (/^\d{14}$/.test(hardwareTime)) {
    return parseCompactHardwareTime(hardwareTime);
  }
  return null;
};

const formatMetric = (value, suffix = '') => {
  if (value === null || value === undefined || value === '') {
    return '--';
  }

  return `${value}${suffix}`;
};

const pickPositiveReading = (...values) => (
  values.find((value) => {
    const numeric = Number(value);
    return Number.isFinite(numeric) && numeric > 0;
  }) ?? null
);

const lifeColor = (lifePercent, status) => {
  if (status === 'not_started') return 'bg-slate-600';
  if (status === 'replace_required' || Number(lifePercent) <= 5) return 'bg-rose-500';
  if (status === 'stock_warning' || Number(lifePercent) <= 20) return 'bg-amber-500';
  return 'bg-emerald-500';
};

const formatDays = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return '--';
  }
  return Math.max(0, Math.round(Number(value)));
};

const CollapsibleSection = ({
  title,
  icon,
  children,
  className = 'admin-card space-y-5 rounded-3xl p-8',
  defaultExpanded = true,
  collapsedSummary = null,
  testId = null
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(defaultExpanded);
  const actionLabel = expanded
    ? t('unitDetail.section.collapse', { defaultValue: 'Collapse section' })
    : t('unitDetail.section.expand', { defaultValue: 'Expand section' });
  const sectionLabel = typeof title === 'string' ? title : '';

  return (
    <section className={className} data-testid={testId || undefined}>
      <div className="flex items-center justify-between gap-4">
        <h3 className="admin-text-secondary flex items-center gap-2 text-xs font-black uppercase tracking-widest">
          {icon}
          {title}
        </h3>
        <button
          type="button"
          onClick={() => setExpanded((value) => !value)}
          aria-expanded={expanded}
          aria-label={sectionLabel ? `${actionLabel}: ${sectionLabel}` : actionLabel}
          className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-slate-900 px-3 py-2 text-xs font-black text-slate-200 transition hover:border-cyan-400/40 hover:text-cyan-200"
        >
          {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          {actionLabel}
        </button>
      </div>
      {expanded ? children : collapsedSummary}
    </section>
  );
};

const TabCollapsiblePanel = ({
  title,
  icon,
  summary,
  children,
  defaultExpanded = true
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(defaultExpanded);
  const actionLabel = expanded
    ? t('unitDetail.section.collapse', { defaultValue: 'Collapse section' })
    : t('unitDetail.section.expand', { defaultValue: 'Expand section' });
  const sectionLabel = typeof title === 'string' ? title : '';

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 rounded-2xl border border-white/5 bg-slate-900/40 p-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h3 className="admin-text-primary flex items-center gap-2 text-sm font-black">
            {icon}
            {title}
          </h3>
          {summary ? (
            <p className="admin-text-secondary mt-1 break-words text-xs leading-relaxed">{summary}</p>
          ) : null}
        </div>
        <button
          type="button"
          onClick={() => setExpanded((value) => !value)}
          aria-expanded={expanded}
          aria-label={sectionLabel ? `${actionLabel}: ${sectionLabel}` : actionLabel}
          className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl border border-white/10 bg-slate-950 px-3 py-2 text-xs font-black text-slate-200 transition hover:border-cyan-400/40 hover:text-cyan-200"
        >
          {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          {actionLabel}
        </button>
      </div>
      {expanded ? children : null}
    </div>
  );
};

const FilterLifecyclePanel = ({ unit, resettingKey, onReset }) => {
  const { t, i18n } = useTranslation();
  const filters = Array.isArray(unit.filters) ? unit.filters : [];
  const mineralFilters = filters.filter((item) => ['shared', 'mineral'].includes(item.routeType));
  const pureFilters = filters.filter((item) => ['shared', 'pure', 'uv'].includes(item.routeType));
  const summary = unit.filterSummary || {};
  const [expanded, setExpanded] = useState(true);

  const renderItem = (filter) => {
    const life = filter.lifePercent === null || filter.lifePercent === undefined ? null : Math.max(0, Math.min(100, Number(filter.lifePercent)));
    return (
      <div key={`${filter.routeType}-${filter.filterKey}`} className="admin-muted-panel rounded-xl p-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-cyan-400/10 text-xs font-black text-cyan-300">
                {filter.stageNo || 'UV'}
              </span>
              <div className="min-w-0">
                <p className="admin-text-primary truncate text-xs font-bold">{getFilterDisplayName(filter, i18n.language)}</p>
                <p className="admin-text-muted text-[10px]">{t(`unitDetail.filterLife.route.${filter.routeType}`, { defaultValue: filter.routeType })}</p>
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => onReset(filter.filterKey)}
            disabled={resettingKey === filter.filterKey}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-800 text-slate-300 transition hover:bg-cyan-400 hover:text-slate-950 disabled:cursor-not-allowed disabled:opacity-50"
            title={t('unitDetail.filterLife.resetTooltip')}
            aria-label={t('unitDetail.filterLife.resetTooltip')}
          >
            <RotateCcw size={14} className={resettingKey === filter.filterKey ? 'animate-spin' : ''} />
          </button>
        </div>
        <div className="admin-text-secondary mt-3 flex items-center justify-between gap-2 text-[10px] font-bold">
          <span>{life === null ? t('unitDetail.filterLife.notReset') : t('unitDetail.filterLife.remaining', { percent: life.toFixed(0) })}</span>
          <span>
            {t('unitDetail.filterLife.timeUsed', {
              used: formatDays(filter.elapsedDays),
              total: formatDays(filter.lifeDays)
            })}
          </span>
        </div>
        <div className="admin-text-muted mt-1 flex items-center justify-between gap-2 text-[10px]">
          <span>{t('unitDetail.filterLife.estimatedReplace')}: {formatLocalizedDateTime(filter.estimatedReplaceAt, i18n.language)}</span>
          <span>
            {t('unitDetail.filterLife.volumeReferenceDetail', {
              used: Number(filter.usedVolumeLiters || 0).toLocaleString(),
              rated: filter.ratedVolumeLiters === null || filter.ratedVolumeLiters === undefined
                ? '--'
                : Number(filter.ratedVolumeLiters).toLocaleString(),
              percent: filter.volumeLifePercent === null || filter.volumeLifePercent === undefined
                ? '--'
                : Math.max(0, Math.min(100, Number(filter.volumeLifePercent))).toFixed(0)
            })}
          </span>
        </div>
        <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-800">
          <div className={`h-full ${lifeColor(life, filter.status)}`} style={{ width: `${life ?? 0}%` }} />
        </div>
        <p className="admin-text-muted mt-2 text-[10px]">{t('unitDetail.filterLife.lastReset')}: {formatLocalizedDateTime(filter.lastResetAt, i18n.language)}</p>
      </div>
    );
  };

  return (
    <section className="admin-card space-y-5 rounded-3xl p-8">
      <div className="flex items-center justify-between gap-4">
        <h3 className="admin-text-secondary flex items-center gap-2 text-xs font-black uppercase tracking-widest">
          <Wrench size={14} />
          {t('unitDetail.filterLife.title')}
        </h3>
        <button
          type="button"
          onClick={() => setExpanded((value) => !value)}
          className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-slate-900 px-3 py-2 text-xs font-black text-slate-200 transition hover:border-cyan-400/40 hover:text-cyan-200"
        >
          {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          {expanded ? t('unitDetail.filterLife.collapse') : t('unitDetail.filterLife.expand')}
        </button>
      </div>

      <p className="admin-text-secondary rounded-xl border border-cyan-400/20 bg-cyan-400/5 px-4 py-3 text-xs leading-relaxed">
        {t('unitDetail.filterLife.usageDefinition')}
      </p>

      <div className="grid grid-cols-3 gap-3">
        <div className="admin-muted-panel rounded-2xl p-3">
          <p className="admin-text-muted text-[10px]">{t('unitDetail.filterLife.lowestLife')}</p>
          <p className="admin-text-primary mt-1 text-xl font-black">{summary.worstLifePercent ?? '--'}%</p>
        </div>
        <div className="admin-muted-panel rounded-2xl p-3">
          <p className="admin-text-muted text-[10px]">{t('unitDetail.filterLife.stockWarning')}</p>
          <p className="mt-1 text-xl font-black text-amber-300">{summary.stockWarningCount || 0}</p>
        </div>
        <div className="admin-muted-panel rounded-2xl p-3">
          <p className="admin-text-muted text-[10px]">{t('unitDetail.filterLife.replaceRequired')}</p>
          <p className="mt-1 text-xl font-black text-rose-300">{summary.replaceRequiredCount || 0}</p>
        </div>
      </div>

      {summary.nextReplaceItem && !expanded ? (
        <div className="rounded-2xl border border-amber-400/20 bg-amber-400/10 p-4">
          <p className="text-[10px] font-black uppercase tracking-widest text-amber-300">{t('unitDetail.filterLife.nextAction')}</p>
          <p className="admin-text-primary mt-2 text-sm font-bold">
            {getFilterDisplayName(summary.nextReplaceItem, i18n.language)}
          </p>
          <p className="admin-text-secondary mt-1 text-xs">{t('unitDetail.filterLife.remaining', { percent: summary.nextReplaceItem.lifePercent ?? '--' })}</p>
        </div>
      ) : null}

      {expanded && filters.length > 0 ? (
        <div className="space-y-5">
          <div>
            <p className="mb-3 text-xs font-black uppercase tracking-widest text-emerald-300">{t('unitDetail.filterLife.mineralRoute')}</p>
            <div className="grid gap-3">{mineralFilters.map(renderItem)}</div>
          </div>
          <div>
            <p className="mb-3 text-xs font-black uppercase tracking-widest text-cyan-300">{t('unitDetail.filterLife.pureRoute')}</p>
            <div className="grid gap-3">{pureFilters.map(renderItem)}</div>
          </div>
        </div>
      ) : expanded ? (
        <div className="admin-muted-panel rounded-2xl border-dashed py-8 text-center text-sm">{t('unitDetail.filterLife.noData')}</div>
      ) : null}
    </section>
  );
};

const deviceSettingFields = [
  { key: 'C1', min: 0, max: 23 },
  { key: 'C2', min: 0, max: 360 },
  { key: 'C3', min: 0, max: 23 },
  { key: 'C4', min: 0, max: 23 }
];

const DeviceAutomationSettingsPanel = ({ unit }) => {
  const { t } = useTranslation();
  const [settings, setSettings] = useState({ C1: 3, C2: 30, C3: 17, C4: 6 });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [message, setMessage] = useState('');

  const applySettings = (data = {}) => {
    setSettings((current) => ({
      ...current,
      C1: data.C1 ?? current.C1,
      C2: data.C2 ?? current.C2,
      C3: data.C3 ?? current.C3,
      C4: data.C4 ?? current.C4
    }));
  };

  const loadSettings = useCallback(async () => {
    if (!unit?.deviceId) return;
    setLoading(true);
    setMessage('');
    try {
      const res = await deviceSettingsAPI.getSettings(unit.deviceId);
      applySettings(res.data?.data || {});
    } catch (error) {
      setMessage(error.response?.data?.message || t('unitDetail.automation.messages.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [unit?.deviceId, t]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleChange = (key, value) => {
    setSettings((current) => ({ ...current, [key]: value }));
  };

  const validate = () => {
    for (const field of deviceSettingFields) {
      const value = Number(settings[field.key]);
      if (!Number.isInteger(value) || value < field.min || value > field.max) {
        return t('unitDetail.automation.messages.validation', {
          field: t(`unitDetail.automation.fields.${field.key}.label`),
          min: field.min,
          max: field.max
        });
      }
    }
    return '';
  };

  const queryDevice = async () => {
    if (!unit?.deviceId) return;
    setLoading(true);
    setMessage('');
    try {
      const res = await deviceSettingsAPI.queryDevice(unit.deviceId);
      applySettings(res.data?.data || {});
      setMessage(res.data?.queryWarning
        ? `${t('unitDetail.automation.messages.querySuccess')} (${res.data.queryWarning})`
        : t('unitDetail.automation.messages.querySuccess'));
    } catch (error) {
      const diagnostics = error.response?.data?.diagnostics;
      const detail = diagnostics
        ? ` TCP: ${diagnostics.tcpConnected ? 'connected' : 'disconnected'}`
        : '';
      setMessage(`${error.response?.data?.message || t('unitDetail.automation.messages.queryFailed')}${detail}`);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    const validationError = validate();
    if (validationError) {
      setMessage(validationError);
      return;
    }

    setSaving(true);
    setMessage('');
    try {
      const payload = deviceSettingFields.reduce((acc, field) => {
        acc[field.key] = Number(settings[field.key]);
        return acc;
      }, {});
      const res = await deviceSettingsAPI.updateSettings(unit.deviceId, payload);
      applySettings(res.data?.data || payload);
      setMessage(res.data?.deviceOffline
        ? t('unitDetail.automation.messages.savedOffline')
        : t('unitDetail.automation.messages.savedOnline'));
    } catch (error) {
      setMessage(error.response?.data?.message || t('unitDetail.automation.messages.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const restoreStandardSettings = async () => {
    if (!unit?.deviceId) return;
    if (!window.confirm(t('unitDetail.automation.confirmRestore'))) return;

    setRestoring(true);
    setMessage('');
    try {
      const res = await deviceSettingsAPI.resetStandard(unit.deviceId);
      applySettings(res.data?.data || { C1: 3, C2: 30, C3: 17, C4: 6 });
      setMessage(res.data?.deviceOffline
        ? t('unitDetail.automation.messages.restoredOffline')
        : t('unitDetail.automation.messages.restoredOnline'));
    } catch (error) {
      setMessage(error.response?.data?.message || t('unitDetail.automation.messages.restoreFailed'));
    } finally {
      setRestoring(false);
    }
  };

  return (
    <CollapsibleSection
      title={t('unitDetail.automation.title')}
      icon={<Settings size={14} className="text-cyan-300" />}
      collapsedSummary={(
        <span className="rounded-full bg-cyan-400/10 px-3 py-1 text-[10px] font-bold text-cyan-200">
          {t('unitDetail.automation.timezone')}
        </span>
      )}
    >
      <span className="inline-flex rounded-full bg-cyan-400/10 px-3 py-1 text-[10px] font-bold text-cyan-200">
        {t('unitDetail.automation.timezone')}
      </span>

      <div className="grid gap-3">
        {deviceSettingFields.map((field) => (
          <label key={field.key} className="admin-muted-panel block rounded-2xl p-4">
            <span className="admin-text-primary text-xs font-bold">{t(`unitDetail.automation.fields.${field.key}.label`)}</span>
            <span className="admin-text-muted mt-1 block text-[11px]">{t(`unitDetail.automation.fields.${field.key}.hint`)}</span>
            <div className="mt-3 flex items-center gap-3">
              <input
                type="number"
                min={field.min}
                max={field.max}
                value={settings[field.key]}
                onChange={(event) => handleChange(field.key, event.target.value)}
                className="min-w-0 flex-1 rounded-xl border border-white/10 bg-slate-950 px-3 py-2 text-sm font-bold text-white outline-none transition focus:border-cyan-400"
              />
              <span className="admin-text-muted w-20 text-right text-xs">{t(`unitDetail.automation.fields.${field.key}.unit`)}</span>
            </div>
          </label>
        ))}
      </div>

      {message ? (
        <div className="admin-muted-panel rounded-2xl px-4 py-3 text-xs">{message}</div>
      ) : null}

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={queryDevice}
          disabled={loading || saving || restoring}
          className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-slate-900 px-4 py-2 text-xs font-black text-slate-200 transition hover:border-cyan-400/40 hover:text-cyan-200 disabled:opacity-60"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          {t('unitDetail.automation.actions.queryDevice')}
        </button>
        <button
          type="button"
          onClick={restoreStandardSettings}
          disabled={loading || saving || restoring}
          className="inline-flex items-center gap-2 rounded-xl border border-amber-400/40 bg-amber-400/10 px-4 py-2 text-xs font-black text-[var(--admin-warning)] transition hover:border-amber-400/70 hover:bg-amber-400/20 disabled:opacity-60"
        >
          <RotateCcw size={14} className={restoring ? 'animate-spin' : ''} />
          {t('unitDetail.automation.actions.restoreStandard')}
        </button>
        <button
          type="button"
          onClick={saveSettings}
          disabled={loading || saving || restoring}
          className="inline-flex items-center gap-2 rounded-xl bg-cyan-400 px-4 py-2 text-xs font-black text-slate-950 transition hover:bg-cyan-300 disabled:opacity-60"
        >
          <Settings size={14} className={saving ? 'animate-spin' : ''} />
          {t('unitDetail.automation.actions.save')}
        </button>
      </div>
    </CollapsibleSection>
  );
};

const WaterQualityAnalysisPanel = ({ analysis }) => {
  const { t } = useTranslation();
  if (!analysis) return null;

  return (
    <CollapsibleSection
      title={t('unitDetail.waterQuality.title')}
      icon={<Gauge size={14} className="text-blue-400" />}
      collapsedSummary={(
        <div className="admin-muted-panel rounded-2xl p-4">
          <p className="admin-text-secondary text-xs font-semibold">{t('unitDetail.waterQuality.estimatedMonthlyCost')}</p>
          <p className="mt-2 text-xl font-black text-[var(--admin-success)]">
            Rp {(analysis.estimatedMonthlyMaintenanceCost || 0).toLocaleString()}
          </p>
        </div>
      )}
    >
      <div className="grid gap-4">
        <div className="admin-muted-panel rounded-2xl p-4">
          <p className="admin-text-secondary text-xs font-semibold">{t('unitDetail.waterQuality.estimatedMonthlyCost')}</p>
          <p className="mt-2 text-xl font-black text-[var(--admin-success)]">
            Rp {(analysis.estimatedMonthlyMaintenanceCost || 0).toLocaleString()}
          </p>
          <p className="admin-text-secondary mt-1 text-xs">{t('unitDetail.waterQuality.filterInspectionRisk')}</p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 text-xs">
        <div className="admin-muted-panel rounded-xl p-3">{t('unitDetail.waterQuality.mineral30d')}: {analysis.mineralVolume30d ?? 0} L</div>
        <div className="admin-muted-panel rounded-xl p-3">{t('unitDetail.waterQuality.pure30d')}: {analysis.pureVolume30d ?? 0} L</div>
      </div>
    </CollapsibleSection>
  );
};

const formatNumberMetric = (value, suffix = '') => {
  const numericValue = Number(value);
  if (value === null || value === undefined || value === '' || Number.isNaN(numericValue)) {
    return '--';
  }
  return `${numericValue.toLocaleString()}${suffix}`;
};

const OperationalAnalysisPanel = ({ analysis, unitId }) => {
  const { t, i18n } = useTranslation();
  const faultTopTypes = analysis?.faultTop3?.topTypes || [];
  const waterRecords = analysis?.waterRecords || {};
  const waterQuality = analysis?.waterQuality || {};
  const availability = analysis?.availability || {};
  const openAlertsHref = buildDeviceAlertHref({ unitId });

  if (!analysis) {
    return (
      <div className="py-12 text-center text-sm text-slate-500">
        {t('unitDetail.operationalAnalysis.empty', { defaultValue: 'No analysis data yet.' })}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className={`rounded-2xl border p-4 text-sm ${
        availability.status === 'insufficient_data'
          ? 'border-amber-400/30 bg-amber-400/10 text-amber-200'
          : 'border-cyan-400/20 bg-cyan-400/5 text-cyan-100'
      }`}>
        <p className="font-black">
          {t(`unitDetail.operationalAnalysis.availability.${availability.status || 'ready'}`)}
        </p>
        <p className="mt-1 text-xs leading-relaxed opacity-80">
          {analysis?.analysisType === 'rule_based' && (
            <>{t('unitDetail.operationalAnalysis.ruleBasedNotice')} · </>
          )}
          {t('unitDetail.operationalAnalysis.dataCutoff', {
            time: formatLocalizedDateTime(analysis.dataCutoffAt, i18n.language)
          })}
        </p>
        {availability.missingSources?.length > 0 && (
          <p className="mt-2 text-xs">
            {t('unitDetail.operationalAnalysis.missingSources', {
              sources: availability.missingSources.join(', ')
            })}
          </p>
        )}
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="admin-muted-panel rounded-2xl p-5">
          <p className="admin-text-secondary text-[11px] font-black uppercase tracking-widest">
            {t('unitDetail.operationalAnalysis.waterRecords', { defaultValue: 'Water records' })}
          </p>
          <p className="mt-3 text-3xl font-black text-cyan-300">{formatNumberMetric(waterRecords.totalCompletedRecords)}</p>
          <p className="admin-text-secondary mt-2 text-xs">
            {formatNumberMetric(waterRecords.totalVolume, ' L')} / Rp {formatNumberMetric(waterRecords.totalRevenue)}
          </p>
        </div>
        <div className="admin-muted-panel rounded-2xl p-5">
          <p className="admin-text-secondary text-[11px] font-black uppercase tracking-widest">
            {t('unitDetail.operationalAnalysis.faultAlerts', { defaultValue: 'Fault alerts' })}
          </p>
          <p className="mt-3 text-3xl font-black text-amber-300">{formatNumberMetric(analysis.faultTop3?.totalAlerts)}</p>
          <p className="admin-text-secondary mt-2 text-xs">
            {t('unitDetail.operationalAnalysis.openAlerts', {
              defaultValue: '{{count}} open',
              count: formatNumberMetric(analysis.faultTop3?.openAlerts)
            })}
          </p>
          {unitId && (
            <Link
              to={openAlertsHref}
              className="mt-4 inline-flex items-center gap-2 rounded-xl border border-amber-400/30 bg-amber-400/10 px-3 py-2 text-xs font-black text-amber-200 transition-colors hover:bg-amber-400/20"
            >
              <BellRing size={14} />
              {t('unitDetail.operationalAnalysis.openAlertList', { defaultValue: 'Open alert list' })}
            </Link>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <section className="admin-card rounded-3xl p-6">
          <h3 className="admin-text-primary flex items-center gap-2 text-sm font-black">
            <AlertTriangle size={16} className="text-amber-300" />
            {t('unitDetail.operationalAnalysis.faultTop3', { defaultValue: 'Fault type TOP3' })}
          </h3>
          <div className="mt-4 space-y-3">
            {faultTopTypes.length > 0 ? faultTopTypes.map((fault) => (
              <div key={fault.key} className="admin-muted-panel rounded-2xl p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p className="font-mono text-sm font-bold text-amber-400">{fault.key}</p>
                    <p className="admin-text-muted mt-1 text-xs">{fault.alertType || '--'}</p>
                  </div>
                  <span className="shrink-0 rounded-full bg-amber-500/15 px-3 py-1 text-xs font-bold text-amber-400">
                    {formatNumberMetric(fault.count)}
                  </span>
                </div>
                <p className="admin-text-secondary mt-3 line-clamp-2 text-xs leading-relaxed">{fault.sampleMessage || '--'}</p>
                <p className="admin-text-muted mt-2 text-[11px]">{formatLocalizedDateTime(fault.latestAt, i18n.language)}</p>
                {unitId && (
                  <Link
                    to={buildDeviceAlertHref({
                      unitId,
                      alertType: fault.alertType,
                      status: Number(fault.openCount || 0) > 0 ? 'open' : 'resolved'
                    })}
                    className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-amber-400/25 px-2.5 py-1.5 text-[11px] font-bold text-amber-300 transition-colors hover:bg-amber-400/10"
                  >
                    <BellRing size={12} />
                    {t('unitDetail.operationalAnalysis.viewMatchingAlerts', { defaultValue: 'View matching alerts' })}
                  </Link>
                )}
              </div>
            )) : (
              <div className="admin-muted-panel rounded-2xl border-dashed p-6 text-center text-sm">
                {t('unitDetail.operationalAnalysis.noFaults', { defaultValue: 'No fault alerts in the selected window.' })}
              </div>
            )}
          </div>
        </section>

        <section className="admin-card rounded-3xl p-6">
          <h3 className="admin-text-primary flex items-center gap-2 text-sm font-black">
            <BarChart3 size={16} className="text-cyan-300" />
            {t('unitDetail.operationalAnalysis.recordAnalysis', { defaultValue: 'Water record analysis' })}
          </h3>
          <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
            <div className="admin-muted-panel rounded-2xl p-4">
              <p className="admin-text-muted text-[11px] uppercase tracking-wide">{t('unitDetail.operationalAnalysis.avgVolume', { defaultValue: 'Avg volume' })}</p>
              <p className="mt-2 font-black text-cyan-400">{formatNumberMetric(waterRecords.averageVolume, ' L')}</p>
            </div>
            <div className="admin-muted-panel rounded-2xl p-4">
              <p className="admin-text-muted text-[11px] uppercase tracking-wide">{t('unitDetail.operationalAnalysis.uniqueUsers', { defaultValue: 'Users / cards' })}</p>
              <p className="mt-2 font-black text-cyan-400">{formatNumberMetric(waterRecords.uniqueUserCount)} / {formatNumberMetric(waterRecords.uniqueCardCount)}</p>
            </div>
            <div className="admin-muted-panel rounded-2xl p-4">
              <p className="admin-text-muted text-[11px] uppercase tracking-wide">{t('unitDetail.operationalAnalysis.ph', { defaultValue: 'pH avg' })}</p>
              <p className="mt-2 font-black text-blue-400">{formatNumberMetric(waterQuality.phAvg)}</p>
            </div>
            <div className="admin-muted-panel rounded-2xl p-4">
              <p className="admin-text-muted text-[11px] uppercase tracking-wide">{t('unitDetail.operationalAnalysis.latestRecord', { defaultValue: 'Latest record' })}</p>
              <p className="admin-text-primary mt-2 text-xs font-bold">{formatLocalizedDateTime(waterRecords.latestRecordAt, i18n.language)}</p>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
            <div className="admin-muted-panel rounded-2xl p-4">
              <p className="admin-text-muted">{t('unitDetail.operationalAnalysis.latestWrTds', { defaultValue: 'Latest WR TDS' })}</p>
              <p className="admin-text-primary mt-2">
                {formatNumberMetric(waterQuality.latestTransactionTds?.rawTds)} / {formatNumberMetric(waterQuality.latestTransactionTds?.pureTds)}
              </p>
            </div>
            <div className="admin-muted-panel rounded-2xl p-4">
              <p className="admin-text-muted">{t('unitDetail.operationalAnalysis.latestLogTds', { defaultValue: 'Latest log TDS/pH' })}</p>
              <p className="admin-text-primary mt-2">
                {formatNumberMetric(waterQuality.latestWaterQualityLog?.rawTds)} / {formatNumberMetric(waterQuality.latestWaterQualityLog?.pureTds)} / {formatNumberMetric(waterQuality.latestWaterQualityLog?.ph)}
              </p>
            </div>
          </div>
        </section>
      </div>

      <UnitPreferencePanel preference={waterRecords.preference} />
    </div>
  );
};

const renderPhotoGallery = (urls = [], t) => {
  if (!urls.length) {
    return (
      <div className="rounded-2xl border border-dashed border-white/10 bg-slate-900/40 px-4 py-6 text-center text-xs text-slate-500">
        {t('unitDetail.inspectionDetail.noPhotos')}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
      {urls.map((url, index) => (
        <a
          key={`${url}-${index}`}
          href={url}
          target="_blank"
          rel="noreferrer"
          className="group overflow-hidden rounded-2xl border border-white/10 bg-slate-900/50"
        >
          <img
            src={url}
            alt={`inspection-${index + 1}`}
            className="h-24 w-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        </a>
      ))}
    </div>
  );
};

export default function UnitDetail() {
  const { t, i18n } = useTranslation();
  const currentUser = useAuthStore((state) => state.user);
  const canControlUnits = hasAdminPermission(currentUser, 'control_units');
  const { id } = useParams();
  const navigate = useNavigate();
  const [unit, setUnit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('maintenance');
  const [transactionPage, setTransactionPage] = useState(1);
  const [resettingKey, setResettingKey] = useState(null);
  const detailLoadedRef = useRef(false);

  const loadUnitDetail = useCallback(async () => {
    const fullPageLoading = !detailLoadedRef.current;
    if (fullPageLoading && transactionPage !== 1) {
      return;
    }

    if (fullPageLoading) {
      setLoading(true);
    } else {
      setTransactionsLoading(true);
    }

    try {
      const res = await unitAPI.getUnitDetail(id, {
        transactionPage,
        transactionLimit: TRANSACTION_PAGE_SIZE
      });
      if (res.data?.data) {
        setUnit(res.data.data);
        detailLoadedRef.current = true;
      }
    } catch (error) {
      console.error('Failed to load unit detail:', error);
    } finally {
      setLoading(false);
      setTransactionsLoading(false);
    }
  }, [id, transactionPage]);

  useEffect(() => {
    detailLoadedRef.current = false;
    setUnit(null);
    setLoading(true);
    setTransactionPage(1);
  }, [id]);

  useEffect(() => {
    loadUnitDetail();
  }, [loadUnitDetail]);

  const handleResetFilter = async (filterKey) => {
    if (!window.confirm(t('unitDetail.filterLife.resetConfirm'))) return;
    setResettingKey(filterKey);
    try {
      const res = await unitAPI.resetUnitFilter(id, filterKey);
      if (res.data?.data) {
        setUnit((prev) => ({ ...prev, ...res.data.data }));
      } else {
        await loadUnitDetail();
      }
    } catch (error) {
      alert(error.response?.data?.message || t('unitDetail.filterLife.resetFailed'));
    } finally {
      setResettingKey(null);
    }
  };

  if (loading) {
    return <div className="p-8 text-cyan-400 animate-pulse">{t('unitDetail.loading')}</div>;
  }

  if (!unit) {
    return <div className="p-8 text-rose-400">{t('unitDetail.notFound')}</div>;
  }

  const inspectionSummary = unit.inspectionSummary || {};
  const inspectionRecords = Array.isArray(unit.inspectionRecords) ? unit.inspectionRecords : [];
  const timelineLogs = Array.isArray(unit.logs) ? unit.logs : [];
  const transactions = Array.isArray(unit.transactions) ? unit.transactions : [];
  const transactionPagination = unit.transactionsPagination || {};
  const totalTransactions = Number.isFinite(Number(transactionPagination.total))
    ? Number(transactionPagination.total)
    : transactions.length;
  const transactionTotalPages = Math.max(1, Number(transactionPagination.totalPages || Math.ceil(totalTransactions / TRANSACTION_PAGE_SIZE) || 1));
  const currentTransactionPage = Math.min(Number(transactionPagination.page || transactionPage), transactionTotalPages);
  const transactionStartIndex = (currentTransactionPage - 1) * TRANSACTION_PAGE_SIZE;
  const paginatedTransactions = transactions;
  const transactionDisplayStart = totalTransactions > 0 ? transactionStartIndex + 1 : 0;
  const transactionDisplayEnd = Math.min(transactionStartIndex + paginatedTransactions.length, totalTransactions);
  const displayUnitId = formatStationDisplayId(unit);
  const fullDeviceId = String(unit.deviceId || unit.unitId || '').trim();
  const detailTitleId = fullDeviceId || displayUnitId;
  const installationTimeLabel = t('unitDetail.installation.installedAt', { defaultValue: 'Installed at' });
  const installationDaysLabel = t('unitDetail.installation.serviceDays', { defaultValue: 'Service days' });
  const installationDisplayTime = unit.installedAt || null;
  const currentPureTds = pickPositiveReading(unit.sensors?.pureTDS, unit.waterQualityCurrent?.pureTds, unit.waterQualityCurrent?.tdsValue);
  const currentRawTds = pickPositiveReading(unit.sensors?.rawTDS, unit.waterQualityCurrent?.rawTds);
  const currentPh = pickPositiveReading(unit.sensors?.ph, unit.waterQualityCurrent?.ph);
  const currentTemp = pickPositiveReading(unit.sensors?.temp, unit.waterQualityCurrent?.temp, unit.waterQualityCurrent?.temperature);
  const qualityMetrics = unit.waterQualityCurrent?.metrics || {};
  const qualityFallbackTime = unit.waterQualityCurrent?.updatedAt || unit.lastHeartbeatAt || null;
  const isUnitOffline = !['Active', 'Online'].includes(unit.status);
  const getTransactionDisplayTime = (tx) => {
    const parsedHardwareTime = parseTransactionHardwareTime(tx?.hardwareTe);
    if (parsedHardwareTime && !Number.isNaN(parsedHardwareTime.getTime())) return parsedHardwareTime;
    return tx?.time || tx?.completedAt || tx?.createdAt;
  };

  return (
    <div className="space-y-8 p-8 text-white">
      <header className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div className="flex items-start gap-4">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="rounded-2xl bg-slate-800 p-3 text-slate-400 transition-all hover:text-white"
          >
            <ArrowLeft size={24} />
          </button>
          <div>
            <div className="flex flex-wrap items-center gap-3">
              <h2 className="break-all text-2xl font-bold sm:text-3xl" title={fullDeviceId || displayUnitId}>
                {t('unitDetail.title', { unitId: detailTitleId })}
              </h2>
              <span className={`rounded px-2 py-0.5 text-[10px] font-bold uppercase ${getUnitStatusClasses(unit.status)}`}>
                {unit.status}
              </span>
            </div>
            {fullDeviceId ? (
              <p className="mt-2 font-mono text-xs text-slate-400">
                {t('unitDetail.deviceShortId', { id: displayUnitId, defaultValue: `Station ID: ${displayUnitId}` })}
              </p>
            ) : null}
            <p className="mt-2 text-xs text-slate-500">
              {t('unitDetail.lastHeartbeat', { time: formatLocalizedDateTime(unit.lastHeartbeatAt, i18n.language) })}
            </p>
            <div className="mt-4 flex flex-wrap gap-3">
              <div className="rounded-2xl border border-white/5 bg-slate-800/70 px-4 py-3">
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">{installationTimeLabel}</p>
                <p className="mt-1 text-sm font-bold text-slate-200">{formatLocalizedDateTime(installationDisplayTime, i18n.language)}</p>
              </div>
              <div className="rounded-2xl border border-white/5 bg-slate-800/70 px-4 py-3">
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">{installationDaysLabel}</p>
                <p className="mt-1 text-sm font-bold text-cyan-300">
                  {unit.serviceDays === null || unit.serviceDays === undefined
                    ? '--'
                    : t('unitDetail.installation.days', { count: unit.serviceDays })}
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-1">
          <CollapsibleSection
            title={t('unitDetail.sensors.title')}
            testId="water-quality-sensors"
            icon={<Activity size={14} className="text-emerald-500" />}
            className="space-y-6 rounded-3xl border border-white/5 border-l-4 border-l-emerald-500 bg-slate-800/50 p-8"
            collapsedSummary={(
              <p className="admin-text-secondary text-xs">
                {t(isUnitOffline ? 'unitDetail.sensors.offlineHint' : 'unitDetail.sensors.onlineHint')}
              </p>
            )}
          >
            <p className={`rounded-xl border px-3 py-2 text-xs font-bold ${
              isUnitOffline
                ? 'border-amber-400/25 bg-amber-400/10 text-amber-200'
                : 'border-emerald-400/25 bg-emerald-400/10 text-emerald-200'
            }`}>
              {t(isUnitOffline ? 'unitDetail.sensors.offlineHint' : 'unitDetail.sensors.onlineHint')}
            </p>

            <div className="space-y-4">
              <WaterQualityReadingCard
                label={t('unitDetail.sensors.currentPureTds')}
                value={currentPureTds}
                unit={t('unitDetail.sensors.ppm')}
                measuredAt={qualityMetrics.pureTds?.measuredAt || qualityFallbackTime}
                isOffline={isUnitOffline}
                valueClassName="text-emerald-400"
                icon={<div className="rounded-lg bg-emerald-500/10 p-2 text-emerald-500"><Droplets size={20} /></div>}
              />
              <WaterQualityReadingCard
                label={t('unitDetail.sensors.currentRawTds')}
                value={currentRawTds}
                unit={t('unitDetail.sensors.ppm')}
                measuredAt={qualityMetrics.rawTds?.measuredAt || qualityFallbackTime}
                isOffline={isUnitOffline}
                valueClassName="text-cyan-300"
                icon={<div className="rounded-lg bg-cyan-500/10 p-2 text-cyan-500"><Droplets size={20} /></div>}
              />
              <WaterQualityReadingCard
                label={t('unitDetail.sensors.ph')}
                value={currentPh}
                measuredAt={qualityMetrics.ph?.measuredAt || qualityFallbackTime}
                isOffline={isUnitOffline}
                valueClassName="text-blue-400"
                icon={<div className="rounded-lg bg-blue-500/10 p-2 text-blue-500"><Gauge size={20} /></div>}
              />
              <WaterQualityReadingCard
                label={t('unitDetail.sensors.temperature')}
                value={currentTemp}
                unit={t('unitDetail.sensors.celsius')}
                measuredAt={qualityMetrics.temp?.measuredAt || qualityFallbackTime}
                isOffline={isUnitOffline}
                icon={<div className="rounded-lg bg-amber-500/10 p-2 text-amber-500"><Thermometer size={20} /></div>}
              />
            </div>
          </CollapsibleSection>

          <WaterQualityAnalysisPanel analysis={unit.waterQualityAnalysis} />
          {canControlUnits && <DeviceAutomationSettingsPanel unit={unit} />}
          {canControlUnits && <FilterLifecyclePanel unit={unit} resettingKey={resettingKey} onReset={handleResetFilter} />}

          <CollapsibleSection
            title={t('unitDetail.inspectionSummary.title')}
            icon={<ClipboardCheck size={14} className="text-cyan-400" />}
            collapsedSummary={(
              <div className="admin-muted-panel rounded-2xl p-4">
                <p className="admin-text-secondary text-xs font-semibold uppercase tracking-wider">{t('unitDetail.inspectionSummary.total')}</p>
                <p className="admin-text-primary mt-2 text-2xl font-black">{inspectionSummary.total || 0}</p>
              </div>
            )}
          >
            <div className="grid grid-cols-2 gap-4">
              <div className="admin-muted-panel rounded-2xl p-4">
                <p className="admin-text-secondary text-xs font-semibold uppercase tracking-wider">{t('unitDetail.inspectionSummary.total')}</p>
                <p className="admin-text-primary mt-2 text-2xl font-black">{inspectionSummary.total || 0}</p>
              </div>
              <div className="admin-muted-panel rounded-2xl p-4">
                <p className="admin-text-secondary text-xs font-semibold uppercase tracking-wider">{t('unitDetail.inspectionSummary.latest')}</p>
                <p className="admin-text-primary mt-2 text-sm font-bold">{formatLocalizedDateTime(inspectionSummary.latestInspectionAt, i18n.language)}</p>
              </div>
              <div className="admin-muted-panel rounded-2xl p-4">
                <p className="admin-text-secondary text-xs font-semibold uppercase tracking-wider">{t('unitDetail.inspectionSummary.reviewStatus')}</p>
                <span className={`mt-2 inline-flex rounded-full px-3 py-1 text-xs font-bold ${getReviewStatusClasses(inspectionSummary.latestReviewStatus)}`}>
                  {t(`unitDetail.reviewStatus.${inspectionSummary.latestReviewStatus || 'no_review'}`)}
                </span>
              </div>
            </div>
          </CollapsibleSection>
        </div>

        <div className="space-y-6 lg:col-span-2">
          <section className="min-h-[540px] rounded-3xl border border-white/5 bg-slate-800/50 p-8">
            <div className="mb-8 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setActiveTab('maintenance')}
                className={`rounded-xl px-4 py-2 text-sm font-bold transition-all ${
                  activeTab === 'maintenance' ? 'bg-cyan-400 text-slate-900' : 'bg-slate-700 text-slate-400 hover:text-white'
                }`}
              >
                <Clock size={14} className="mr-1 inline" />
                {t('unitDetail.maintenance.title')}
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('inspection')}
                className={`rounded-xl px-4 py-2 text-sm font-bold transition-all ${
                  activeTab === 'inspection' ? 'bg-cyan-400 text-slate-900' : 'bg-slate-700 text-slate-400 hover:text-white'
                }`}
              >
                <ClipboardCheck size={14} className="mr-1 inline" />
                {t('unitDetail.tabs.inspection')}
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('transactions')}
                className={`rounded-xl px-4 py-2 text-sm font-bold transition-all ${
                  activeTab === 'transactions' ? 'bg-cyan-400 text-slate-900' : 'bg-slate-700 text-slate-400 hover:text-white'
                }`}
              >
                <Droplets size={14} className="mr-1 inline" />
                {t('unitDetail.tabs.transactions')}
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('analysis')}
                className={`rounded-xl px-4 py-2 text-sm font-bold transition-all ${
                  activeTab === 'analysis' ? 'bg-cyan-400 text-slate-900' : 'bg-slate-700 text-slate-400 hover:text-white'
                }`}
              >
                <BarChart3 size={14} className="mr-1 inline" />
                {t('unitDetail.tabs.analysis', { defaultValue: 'Analysis' })}
              </button>
            </div>

            {activeTab === 'maintenance' && (
              <TabCollapsiblePanel
                title={t('unitDetail.maintenance.title')}
                icon={<Clock size={16} className="text-cyan-300" />}
                summary={t('unitDetail.sectionSummary.maintenance', {
                  count: timelineLogs.length,
                  latest: timelineLogs[0]?.date || '--',
                  defaultValue: '{{count}} logs · latest {{latest}}'
                })}
              >
                <div className="relative">
                  <div className="absolute bottom-0 left-[47px] top-0 w-[2px] bg-white/5" />
                  <div className="relative space-y-8">
                    {timelineLogs.length > 0 ? (
                      timelineLogs.map((log, index) => (
                        <div key={log.id || `${log.date}-${index}`} className="group flex gap-8">
                          <div className="w-24 pt-1 text-right text-[10px] font-bold uppercase leading-relaxed tracking-tighter text-slate-500">
                            {log.date}
                          </div>

                          <div className="relative">
                            <div className={`relative z-10 mt-2.5 h-3 w-3 rounded-full ${
                              index === 0 ? 'bg-cyan-400 shadow-[0_0_8px_rgba(34,211,238,0.5)]' : 'bg-slate-700'
                            }`}
                            />
                          </div>

                          <div className="flex-1 rounded-2xl border border-white/5 bg-slate-900/50 p-6 transition-all group-hover:border-cyan-400/20 group-hover:bg-slate-900">
                            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                              <div>
                                <p className={`font-bold ${
                                  String(log.title || '').includes('Inspection') || String(log.title || '').includes('Check-in')
                                    ? 'text-emerald-400'
                                    : 'text-slate-100'
                                }`}>
                                  {log.title}
                                </p>
                                <p className="mt-2 text-xs italic leading-relaxed text-slate-400">
                                  "{log.desc}"
                                </p>
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {log.reviewStatus && (
                                  <span className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase ${getReviewStatusClasses(log.reviewStatus)}`}>
                                    {getStatusLabel(t, 'unitDetail.reviewStatus', log.reviewStatus)}
                                  </span>
                                )}
                                <span className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase ${getOverallStatusClasses()}`}>
                                  {getStatusLabel(t, 'unitDetail.overallStatus', getInspectionDisplayOverallStatus(log) || 'normal')}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="py-12 text-center text-sm text-slate-500">
                        {t('unitDetail.maintenance.noLogs')}
                      </div>
                    )}
                  </div>
                </div>
              </TabCollapsiblePanel>
            )}

            {activeTab === 'inspection' && (
              <TabCollapsiblePanel
                title={t('unitDetail.tabs.inspection')}
                icon={<ClipboardCheck size={16} className="text-cyan-300" />}
                summary={t('unitDetail.sectionSummary.inspection', {
                  count: inspectionRecords.length,
                  latest: formatLocalizedDateTime(inspectionSummary.latestInspectionAt, i18n.language),
                  defaultValue: '{{count}} records · latest {{latest}}'
                })}
              >
                <div className="space-y-6">
                {inspectionRecords.length > 0 ? (
                  inspectionRecords.map((record) => {
                    const photos = extractPhotoUrls(record);
                    const notes = Array.isArray(record.items)
                      ? record.items.filter((item) => item?.notes)
                      : [];
                    const itemDetails = Array.isArray(record.items) ? record.items : [];

                    return (
                      <article
                        key={record.id}
                        className="admin-card space-y-5 rounded-3xl p-6"
                      >
                        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                          <div className="space-y-2">
                            <div className="flex flex-wrap items-center gap-3">
                              <h3 className="admin-text-primary text-lg font-bold">
                                {t('unitDetail.inspectionDetail.time', { time: formatLocalizedDateTime(record.createdAt, i18n.language) })}
                              </h3>
                              <span className={`rounded-full px-3 py-1 text-xs font-bold ${getReviewStatusClasses(record.reviewStatus)}`}>
                                {getStatusLabel(t, 'unitDetail.reviewStatus', record.reviewStatus || 'pending_review')}
                              </span>
                              <span className={`rounded-full px-3 py-1 text-xs font-bold ${getOverallStatusClasses()}`}>
                                {getStatusLabel(t, 'unitDetail.overallStatus', getInspectionDisplayOverallStatus(record) || 'normal')}
                              </span>
                            </div>
                            <p className="admin-text-secondary text-sm">
                              {t('unitDetail.inspectionDetail.steward', { name: record.stewardName || unit.steward?.name || '--' })}
                            </p>
                            <p className="admin-text-secondary text-sm leading-relaxed">
                              {getInspectionDisplaySummary(t, record)}
                            </p>
                          </div>

                          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                            <div className="admin-panel-muted rounded-2xl p-3">
                              <p className="admin-text-muted text-[11px] uppercase tracking-wide">{t('unitDetail.inspectionDetail.pureTds')}</p>
                              <p className="mt-2 text-lg font-black text-emerald-300">{formatMetric(record.tdsValue)}</p>
                            </div>
                            <div className="admin-panel-muted rounded-2xl p-3">
                              <p className="admin-text-muted text-[11px] uppercase tracking-wide">{t('unitDetail.inspectionDetail.rawTds')}</p>
                              <p className="mt-2 text-lg font-black text-cyan-300">{formatMetric(record.rawTdsValue)}</p>
                            </div>
                            <div className="admin-panel-muted rounded-2xl p-3">
                              <p className="admin-text-muted text-[11px] uppercase tracking-wide">pH</p>
                              <p className="mt-2 text-lg font-black text-blue-300">{formatMetric(record.phValue)}</p>
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                          <div className="admin-panel-muted rounded-2xl p-4">
                            <p className="admin-text-muted text-[11px] uppercase tracking-wide">{t('unitDetail.inspectionDetail.cleaningStatus')}</p>
                            <div className="admin-text-primary mt-3 flex items-center gap-2 text-sm font-bold">
                              {record.cleaned ? <CheckCircle2 size={16} className="text-emerald-300" /> : <XCircle size={16} className="text-rose-300" />}
                              {record.cleaned ? t('unitDetail.inspectionDetail.completed') : t('unitDetail.inspectionDetail.incomplete')}
                            </div>
                          </div>
                          <div className="admin-panel-muted rounded-2xl p-4">
                            <p className="admin-text-muted text-[11px] uppercase tracking-wide">{t('unitDetail.inspectionDetail.filterCheck')}</p>
                            <div className="admin-text-primary mt-3 flex items-center gap-2 text-sm font-bold">
                              {record.filterChecked ? <CheckCircle2 size={16} className="text-emerald-300" /> : <XCircle size={16} className="text-rose-300" />}
                              {record.filterChecked ? t('unitDetail.inspectionDetail.completed') : t('unitDetail.inspectionDetail.incomplete')}
                            </div>
                          </div>
                          <div className="admin-panel-muted rounded-2xl p-4">
                            <p className="admin-text-muted text-[11px] uppercase tracking-wide">{t('unitDetail.inspectionDetail.leakageCheck')}</p>
                            <div className="admin-text-primary mt-3 flex items-center gap-2 text-sm font-bold">
                              {record.leakageChecked ? <CheckCircle2 size={16} className="text-emerald-300" /> : <XCircle size={16} className="text-rose-300" />}
                              {record.leakageChecked ? t('unitDetail.inspectionDetail.completed') : t('unitDetail.inspectionDetail.incomplete')}
                            </div>
                          </div>
                        </div>

                        {(record.reviewNote || record.rejectionReason || notes.length > 0) && (
                          <div className="admin-panel-muted space-y-3 rounded-2xl p-5">
                            <div className="admin-text-primary flex items-center gap-2 text-sm font-bold">
                              <AlertTriangle size={16} className="text-amber-300" />
                              {t('unitDetail.inspectionDetail.notesReview')}
                            </div>
                            {record.reviewNote && (
                              <p className="admin-text-secondary text-sm">{t('unitDetail.inspectionDetail.reviewNote', { note: record.reviewNote })}</p>
                            )}
                            {record.rejectionReason && (
                              <p className="text-sm text-rose-300">{t('unitDetail.inspectionDetail.rejectionReason', { reason: record.rejectionReason })}</p>
                            )}
                            {notes.length > 0 && (
                              <div className="space-y-2">
                                {notes.map((item) => (
                                  <div key={item.id || item.itemId} className="admin-card rounded-xl p-3">
                                    <p className="admin-text-primary text-sm font-bold">{getInspectionItemText(t, item, 'title')}</p>
                                    <p className="admin-text-secondary mt-1 text-sm">{getInspectionFreeText(t, item.notes)}</p>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                        {itemDetails.length > 0 && (
                          <div className="admin-panel-muted space-y-3 rounded-2xl p-5">
                            <div className="admin-text-primary flex items-center gap-2 text-sm font-bold">
                              <ClipboardCheck size={16} className="text-cyan-300" />
                              {t('unitDetail.inspectionDetail.itemDetails')}
                            </div>
                            <div className="space-y-3">
                              {itemDetails.map((item) => {
                                const watermarkMeta = getWatermarkMeta(t, item.watermarkStatus);
                                return (
                                  <div
                                    key={item.id || item.itemId}
                                    className="admin-card rounded-2xl p-4"
                                  >
                                    <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                                      <div>
                                        <p className="admin-text-primary text-sm font-bold">{getInspectionItemText(t, item, 'title')}</p>
                                        {item.description ? (
                                          <p className="admin-text-secondary mt-1 text-xs leading-relaxed">{getInspectionItemText(t, item, 'description')}</p>
                                        ) : null}
                                      </div>
                                      <div className="flex flex-wrap gap-2">
                                        <span className={`rounded-full px-3 py-1 text-[10px] font-bold ${getReviewStatusClasses(item.reviewStatus)}`}>
                                          {getStatusLabel(t, 'unitDetail.reviewStatus', item.reviewStatus || 'pending_review')}
                                        </span>
                                        <span className={`rounded-full px-3 py-1 text-[10px] font-bold ${watermarkMeta.className}`}>
                                          {watermarkMeta.label}
                                        </span>
                                      </div>
                                    </div>
                                    <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
                                      <div className="admin-panel-muted rounded-xl p-3">
                                        <p className="admin-text-muted text-[11px] uppercase tracking-wide">{t('unitDetail.inspectionDetail.measurement')}</p>
                                        <p className="admin-text-primary mt-2 text-sm font-bold">
                                          {item.measurementValue ?? '--'} {item.measurementUnit || ''}
                                        </p>
                                      </div>
                                      <div className="admin-panel-muted rounded-xl p-3">
                                        <p className="admin-text-muted text-[11px] uppercase tracking-wide">{t('unitDetail.inspectionDetail.notes')}</p>
                                        <p className="admin-text-secondary mt-2 text-sm">{item.notes ? getInspectionFreeText(t, item.notes) : t('common.none', { defaultValue: '-' })}</p>
                                      </div>
                                      <div className="admin-panel-muted rounded-xl p-3">
                                        <p className="admin-text-muted text-[11px] uppercase tracking-wide">{t('unitDetail.inspectionDetail.photoCount')}</p>
                                        <p className="admin-text-primary mt-2 text-sm font-bold">{item.photoUrls?.length || 0}</p>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        <div className="space-y-3">
                          <div className="admin-text-primary flex items-center gap-2 text-sm font-bold">
                            <ImageIcon size={16} className="text-cyan-300" />
                            {t('unitDetail.inspectionDetail.photos', { count: photos.length })}
                          </div>
                          {renderPhotoGallery(photos, t)}
                        </div>
                      </article>
                    );
                  })
                ) : (
                  <div className="py-12 text-center text-sm text-slate-500">
                    {t('unitDetail.inspectionDetail.empty')}
                  </div>
                )}
                </div>
              </TabCollapsiblePanel>
            )}

            {activeTab === 'transactions' && (
              <TabCollapsiblePanel
                title={t('unitDetail.tabs.transactions')}
                icon={<Droplets size={16} className="text-cyan-300" />}
                summary={t('unitDetail.sectionSummary.transactions', {
                  start: transactionDisplayStart,
                  end: transactionDisplayEnd,
                  total: totalTransactions,
                  defaultValue: 'Showing {{start}}-{{end}} of {{total}} records'
                })}
              >
                <div className="space-y-4">
                {transactionsLoading && (
                  <div className="inline-flex items-center gap-2 rounded-full border border-cyan-300/30 bg-cyan-300/10 px-3 py-1 text-xs font-bold text-cyan-300">
                    <Clock size={14} className="animate-pulse" />
                    {t('common.loading', { defaultValue: 'Loading...' })}
                  </div>
                )}
                {transactions.length > 0 ? (
                  <>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-white/5 text-[10px] uppercase tracking-widest text-slate-500">
                            <th className="pb-3 pr-4 text-left">{t('unitDetail.transactions.time')}</th>
                            <th className="px-3 pb-3 text-left">
                              {t('unitDetail.transactions.userCardNumber', { defaultValue: `${t('unitDetail.transactions.user')} / Card No.` })}
                            </th>
                            <th className="px-3 pb-3 text-left">{t('unitDetail.transactions.cardType')}</th>
                            <th className="px-3 pb-3 text-right">{t('unitDetail.transactions.volume')}</th>
                            <th className="min-w-[140px] px-4 pb-3 text-right">{t('unitDetail.transactions.amount')}</th>
                            <th className="px-3 pb-3 text-left">{t('unitDetail.transactions.waterType')}</th>
                            <th className="pl-3 pb-3 text-left">{t('unitDetail.transactions.status')}</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-white/5">
                          {paginatedTransactions.map((tx) => (
                            <tr key={tx.id} className="text-slate-300 transition-colors hover:bg-slate-700/30">
                              <td className="py-3 pr-4 text-xs text-slate-400">{formatLocalizedDateTime(getTransactionDisplayTime(tx), i18n.language)}</td>
                              <td className="min-w-[180px] px-3 py-3">
                                <div className="font-medium text-slate-200">{tx.userName || '--'}</div>
                                <div className="mt-1 font-mono text-[11px] text-slate-500" title={getTransactionCardTitle(tx)}>
                                  {getTransactionCardDisplay(tx)}
                                </div>
                              </td>
                              <td className="px-3 py-3">
                                <span title={tx.cardType} className={`rounded px-2 py-0.5 text-[10px] font-bold ${
                                  tx.cardType === 'Physical' ? 'bg-amber-500/20 text-amber-400' : 'bg-purple-500/20 text-purple-400'
                                }`}>
                                  {getTransactionCardTypeCode(tx.cardType)}
                                </span>
                              </td>
                              <td className="px-3 py-3 text-right font-bold text-cyan-400">{tx.volume ?? '--'}</td>
                              <td className="min-w-[140px] whitespace-nowrap px-4 py-3 text-right font-bold text-emerald-400">{tx.amount?.toLocaleString?.() || tx.amount}</td>
                              <td className="px-3 py-3 whitespace-nowrap">{getWaterTypeLabel(t, tx.waterType)}</td>
                              <td className="pl-3 py-3">
                                <span title={tx.status} className={`rounded px-2 py-0.5 text-[10px] font-bold ${
                                  tx.status === 'Completed' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'
                                }`}>
                                  {getTransactionStatusCode(tx.status)}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {transactionTotalPages > 1 && (
                      <div className="flex flex-col gap-3 border-t border-white/5 pt-4 text-xs text-slate-400 sm:flex-row sm:items-center sm:justify-between">
                        <span>
                          {t('unitDetail.transactions.pageInfo', {
                            start: transactionDisplayStart,
                            end: transactionDisplayEnd,
                            total: totalTransactions,
                            page: currentTransactionPage,
                            totalPages: transactionTotalPages
                          })}
                        </span>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            aria-label={t('common.previous')}
                            title={t('common.previous')}
                            disabled={transactionsLoading || currentTransactionPage <= 1}
                            onClick={() => setTransactionPage((page) => Math.max(1, page - 1))}
                            className="rounded-lg border border-white/10 bg-slate-900 p-2 text-slate-400 transition-colors hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
                          >
                            <ChevronLeft size={16} />
                          </button>
                          <button
                            type="button"
                            aria-label={t('common.next')}
                            title={t('common.next')}
                            disabled={transactionsLoading || currentTransactionPage >= transactionTotalPages}
                            onClick={() => setTransactionPage((page) => Math.min(transactionTotalPages, page + 1))}
                            className="rounded-lg border border-white/10 bg-slate-900 p-2 text-slate-400 transition-colors hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
                          >
                            <ChevronRight size={16} />
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="py-12 text-center text-sm text-slate-500">{t('unitDetail.transactions.empty')}</div>
                )}
                </div>
              </TabCollapsiblePanel>
            )}

            {activeTab === 'analysis' && (
              <TabCollapsiblePanel
                title={t('unitDetail.tabs.analysis', { defaultValue: 'Analysis' })}
                icon={<BarChart3 size={16} className="text-cyan-300" />}
                summary={t('unitDetail.sectionSummary.analysis', {
                  records: formatNumberMetric(unit.unitOperationalAnalysis?.waterRecords?.totalCompletedRecords),
                  alerts: formatNumberMetric(unit.unitOperationalAnalysis?.faultTop3?.totalAlerts),
                  defaultValue: '{{records}} records · {{alerts}} alerts'
                })}
              >
                <OperationalAnalysisPanel analysis={unit.unitOperationalAnalysis} unitId={unit.id} />
              </TabCollapsiblePanel>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
